import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import type { Podcast } from '../types/database'
import { useAuth } from './AuthContext'
import { getPodcasts } from '../lib/api'

interface PodcastContextType {
  podcasts: Podcast[]
  selectedPodcast: Podcast | null
  setSelectedPodcast: (podcast: Podcast) => void
  loading: boolean
}

const PodcastContext = createContext<PodcastContextType | undefined>(undefined)

const SELECTED_PODCAST_KEY = 'grasppod-selected-podcast-id'

export function PodcastProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  const [podcasts, setPodcasts] = useState<Podcast[]>([])
  const [selectedPodcast, setSelectedPodcastState] = useState<Podcast | null>(null)
  const [loading, setLoading] = useState(true)

  const setSelectedPodcast = useCallback((podcast: Podcast) => {
    setSelectedPodcastState(podcast)
    localStorage.setItem(SELECTED_PODCAST_KEY, podcast.id)
  }, [])

  useEffect(() => {
    if (!session) {
      setPodcasts([])
      setSelectedPodcastState(null)
      setLoading(false)
      return
    }

    let mounted = true

    async function fetchPodcasts() {
      try {
        const data = await getPodcasts()
        if (!mounted) return

        setPodcasts(data)

        // Auto-select: try localStorage first, then first podcast
        const savedId = localStorage.getItem(SELECTED_PODCAST_KEY)
        const saved = data.find((p) => p.id === savedId)

        if (saved) {
          setSelectedPodcastState(saved)
        } else if (data.length > 0) {
          setSelectedPodcastState(data[0])
          localStorage.setItem(SELECTED_PODCAST_KEY, data[0].id)
        }
      } catch {
        // Silently fail — podcasts list stays empty
      } finally {
        if (mounted) setLoading(false)
      }
    }

    fetchPodcasts()
    return () => { mounted = false }
  }, [session])

  return (
    <PodcastContext.Provider value={{ podcasts, selectedPodcast, setSelectedPodcast, loading }}>
      {children}
    </PodcastContext.Provider>
  )
}

export function usePodcast(): PodcastContextType {
  const context = useContext(PodcastContext)
  if (!context) throw new Error('usePodcast must be used within a PodcastProvider')
  return context
}
